import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';

// Toast Context
const ToastContext = createContext(null);

// Toast Provider Component
export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const addToast = useCallback((message, type = 'info', duration = 4000) => {
        const id = Date.now() + Math.random();
        const toast = { id, message, type, duration };
        setToasts(prev => [...prev, toast]);

        // Auto remove after duration
        if (duration > 0) {
            setTimeout(() => {
                setToasts(prev => prev.filter(t => t.id !== id));
            }, duration);
        }

        return id;
    }, []);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    }, []);

    const toast = {
        success: (msg, duration) => addToast(msg, 'success', duration),
        error: (msg, duration) => addToast(msg, 'error', duration),
        warning: (msg, duration) => addToast(msg, 'warning', duration),
        info: (msg, duration) => addToast(msg, 'info', duration),
    };

    return (
        <ToastContext.Provider value={{ toast, removeToast }}>
            {children}
            <ToastContainer toasts={toasts} removeToast={removeToast} />
        </ToastContext.Provider>
    );
};

// Hook to use toast
export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        // Fallback if used outside provider - use console and return dummy
        return {
            toast: {
                success: (msg) => console.log('✅', msg),
                error: (msg) => console.error('❌', msg),
                warning: (msg) => console.warn('⚠️', msg),
                info: (msg) => console.info('ℹ️', msg),
            }
        };
    }
    return context;
};

// Toast Container Component
const ToastContainer = ({ toasts, removeToast }) => {
    return (
        <div className="fixed top-4 right-4 z-[9999] flex flex-col gap-3 max-w-md">
            {toasts.map(toast => (
                <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
            ))}
        </div>
    );
};

// Individual Toast Item
const ToastItem = ({ toast, onClose }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [isLeaving, setIsLeaving] = useState(false);

    useEffect(() => {
        // Animate in
        setTimeout(() => setIsVisible(true), 10);
    }, []);

    const handleClose = () => {
        setIsLeaving(true);
        setTimeout(onClose, 300);
    };

    const typeStyles = {
        success: {
            bg: 'bg-gradient-to-r from-green-500 to-emerald-600',
            icon: '✅',
            border: 'border-green-400',
        },
        error: {
            bg: 'bg-gradient-to-r from-red-500 to-rose-600',
            icon: '❌',
            border: 'border-red-400',
        },
        warning: {
            bg: 'bg-gradient-to-r from-amber-500 to-orange-600',
            icon: '⚠️',
            border: 'border-amber-400',
        },
        info: {
            bg: 'bg-gradient-to-r from-blue-500 to-indigo-600',
            icon: 'ℹ️',
            border: 'border-blue-400',
        },
    };

    const style = typeStyles[toast.type] || typeStyles.info;

    return (
        <div
            className={`
        ${style.bg} ${style.border}
        text-white px-4 py-3 rounded-lg shadow-2xl border-l-4
        flex items-start gap-3 min-w-[300px] max-w-md
        transform transition-all duration-300 ease-out
        ${isVisible && !isLeaving ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
        >
            <span className="text-xl flex-shrink-0">{style.icon}</span>
            <div className="flex-1 text-sm font-medium leading-relaxed whitespace-pre-line">
                {toast.message}
            </div>
            <button
                onClick={handleClose}
                className="text-white/80 hover:text-white text-lg font-bold flex-shrink-0 hover:scale-110 transition-transform"
            >
                ✕
            </button>
        </div>
    );
};

// Simple standalone toast function for non-React contexts
let toastQueue = [];
let toastContainer = null;

export const showToast = (message, type = 'info', duration = 4000) => {
    // Create container if doesn't exist
    if (!toastContainer) {
        toastContainer = document.createElement('div');
        toastContainer.id = 'toast-standalone-container';
        toastContainer.className = 'fixed top-4 right-4 z-[9999] flex flex-col gap-3 max-w-md';
        document.body.appendChild(toastContainer);
    }

    const toastId = Date.now() + Math.random();

    const typeStyles = {
        success: { bg: 'linear-gradient(to right, #22c55e, #059669)', icon: '✅', border: '#4ade80' },
        error: { bg: 'linear-gradient(to right, #ef4444, #e11d48)', icon: '❌', border: '#f87171' },
        warning: { bg: 'linear-gradient(to right, #f59e0b, #ea580c)', icon: '⚠️', border: '#fbbf24' },
        info: { bg: 'linear-gradient(to right, #3b82f6, #6366f1)', icon: 'ℹ️', border: '#60a5fa' },
    };

    const style = typeStyles[type] || typeStyles.info;

    const toastEl = document.createElement('div');
    toastEl.id = `toast-${toastId}`;
    toastEl.style.cssText = `
    background: ${style.bg};
    border-left: 4px solid ${style.border};
    color: white;
    padding: 12px 16px;
    border-radius: 8px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-width: 300px;
    max-width: 400px;
    transform: translateX(120%);
    opacity: 0;
    transition: all 0.3s ease-out;
  `;

    toastEl.innerHTML = `
    <span style="font-size: 20px; flex-shrink: 0;">${style.icon}</span>
    <div style="flex: 1; font-size: 14px; font-weight: 500; line-height: 1.5; white-space: pre-line;">${message}</div>
    <button onclick="this.parentElement.remove()" style="color: rgba(255,255,255,0.8); font-size: 18px; font-weight: bold; flex-shrink: 0; cursor: pointer; background: none; border: none;">✕</button>
  `;

    toastContainer.appendChild(toastEl);

    // Animate in
    setTimeout(() => {
        toastEl.style.transform = 'translateX(0)';
        toastEl.style.opacity = '1';
    }, 10);

    // Auto remove
    if (duration > 0) {
        setTimeout(() => {
            toastEl.style.transform = 'translateX(120%)';
            toastEl.style.opacity = '0';
            setTimeout(() => toastEl.remove(), 300);
        }, duration);
    }

    return toastId;
};

// Global toast object for easy access
export const toast = {
    success: (msg, duration) => showToast(msg, 'success', duration),
    error: (msg, duration) => showToast(msg, 'error', duration),
    warning: (msg, duration) => showToast(msg, 'warning', duration),
    info: (msg, duration) => showToast(msg, 'info', duration),
};

export default ToastProvider;
